import { useState, useCallback, useRef, useEffect } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, shareBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { RotateCw } from "lucide-react";
import { toast } from "sonner";
import { scanDocument, perspectiveCorrect, type QuadCorners } from "./operations";
import { PDFDocument } from "pdf-lib";
import { detectDocumentQuad, flattenIllumination, pdfPageFor, readExifOrientation, exifTransform, isLowResolution, isNearlyBlack, movePage, defaultPageSizeForLocale, type ScanPageSize, type ToneInfo } from "./scannerUtils";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Turns a photo of a document into a clean, high-contrast "scanned" look — upload a photo or capture one with your camera, crop it down to just the document, straighten it if it was photographed at an angle, then enhance it and rotate if needed. Runs entirely in your browser — the photo never leaves your device.`,
  how: `Upload a photo of a document or use your camera to take one. Drag to crop out anything that isn't the document, then drag the four corner handles onto the document's actual corners if the photo was taken at an angle (skip this if it's already straight-on). Adjust contrast, rotate if needed, then download as an image or save as a PDF.`,
  tip: `The page's four corners are found automatically when the page is lighter than what's behind it (a white sheet on a darker table works best). On a busy or light background the detection can miss, so the corners are always draggable: place them on the actual corners of the page for an exact result. Shadow removal evens out uneven lighting; turn it off for photos that are already evenly lit. Use "Add page & scan next" to build a multi-page PDF.`,
};

type Stage = "select" | "crop" | "straighten" | "enhance";
type Rotation = 0 | 90 | 180 | 270;

export default function DocumentScanner() {
  const [stage, setStage] = useState<Stage>("select");
  const [fileName, setFileName] = useState("document");

  // Raw source, before any cropping/straightening.
  const [rawUrl, setRawUrl] = useState<string | null>(null);
  const [selection, setSelection] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);

  // After crop, before straighten.
  const [croppedUrl, setCroppedUrl] = useState<string | null>(null);
  const [corners, setCorners] = useState<QuadCorners | null>(null);
  const [draggingCorner, setDraggingCorner] = useState<keyof QuadCorners | null>(null);

  const [contrast, setContrast] = useState([60]);
  const [grayscale, setGrayscale] = useState(true);
  const [rotation, setRotation] = useState<Rotation>(0);
  const [removeShadows, setRemoveShadows] = useState(true);
  const [pageSize, setPageSize] = useState<ScanPageSize>(() => defaultPageSizeForLocale(typeof navigator !== "undefined" ? navigator.language : ""));
  const [pages, setPages] = useState<Blob[]>([]); // earlier pages of the same PDF (JPEG)
  const [autoNote, setAutoNote] = useState("");
  const [lowResNote, setLowResNote] = useState("");
  const [toneNote, setToneNote] = useState("");
  const { logUsageNow } = useLogUsageOnAction();

  const rawImgRef = useRef<HTMLImageElement | null>(null);
  const rawContainerRef = useRef<HTMLDivElement | null>(null);
  const straightenImgRef = useRef<HTMLImageElement | null>(null);
  const straightenContainerRef = useRef<HTMLDivElement | null>(null);
  const enhanceSourceImgRef = useRef<HTMLImageElement | null>(null); // the finalized, straightened source
  const enhanceCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [resultPngBlob, setResultPngBlob] = useState<Blob | null>(null);
  const [resultPdfBlob, setResultPdfBlob] = useState<Blob | null>(null);

  // Bakes the EXIF orientation into the pixels so a sideways phone photo is upright
  // from the first step on. Falls back to the plain file if the browser can't decode it this way.
  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0]; if (!f) return;
    setFileName(f.name.replace(/\.[^.]+$/, ""));
    setSelection(null);
    let url = "";
    let w = 0, h = 0;
    try {
      const orientation = readExifOrientation(await f.arrayBuffer());
      const bmp = await createImageBitmap(f, { imageOrientation: "none" });
      const t = exifTransform(orientation, bmp.width, bmp.height);
      const c = document.createElement("canvas");
      c.width = t.width; c.height = t.height;
      const cx = c.getContext("2d")!;
      cx.setTransform(...t.m);
      cx.drawImage(bmp, 0, 0);
      bmp.close?.();
      const blob = await new Promise<Blob | null>(res => c.toBlob(res, "image/png"));
      if (blob) { url = URL.createObjectURL(blob); w = t.width; h = t.height; }
    } catch (err) { console.warn("Orientation fix skipped", err); }
    if (!url) {
      url = URL.createObjectURL(f);
      const probe = new Image();
      await new Promise<void>(res => { probe.onload = probe.onerror = () => res(); probe.src = url; });
      w = probe.naturalWidth; h = probe.naturalHeight;
    }
    setLowResNote(w > 0 && isLowResolution(w, h)
      ? `This photo is small (${w} x ${h} px). Text may look blurry or pixelated. A photo with at least 600 px on the short side, ideally much more, gives a better scan.`
      : "");
    setRawUrl(url);
    setStage("crop");
  }, []);

  // --- Crop stage ---
  // The <img> elements here use object-contain, which means whenever a
  // photo's aspect ratio doesn't exactly match its display box, the
  // browser letterboxes it (empty space on the sides or top/bottom) --
  // but getBoundingClientRect() on the element still reports the FULL
  // box, not the smaller, centered area where the image is actually
  // visible. The same real, confirmed bug already found and fixed in
  // Screenshot Tool: coordinate math measured against the full box
  // produces a shifted/wrong crop selection whenever a photo's aspect
  // ratio doesn't happen to match the box exactly -- which is the
  // common case for a photo taken freehand, not the exception.
  const getContainedImageRect = (img: HTMLImageElement) => {
    const rect = img.getBoundingClientRect();
    const boxRatio = rect.width / rect.height;
    const imgRatio = img.naturalWidth / img.naturalHeight;
    let width: number, height: number, x: number, y: number;
    if (imgRatio > boxRatio) {
      width = rect.width;
      height = rect.width / imgRatio;
      x = 0;
      y = (rect.height - height) / 2;
    } else {
      height = rect.height;
      width = rect.height * imgRatio;
      y = 0;
      x = (rect.width - width) / 2;
    }
    return { x, y, width, height };
  };
  const getRelativePosAt = (imgRef: React.RefObject<HTMLImageElement | null>, clientX: number, clientY: number) => {
    const img = imgRef.current;
    if (!img) return { x: 0, y: 0 };
    const rect = img.getBoundingClientRect();
    const content = getContainedImageRect(img);
    const rawX = clientX - rect.left - content.x;
    const rawY = clientY - rect.top - content.y;
    return {
      x: Math.max(0, Math.min(content.width, rawX)),
      y: Math.max(0, Math.min(content.height, rawY)),
    };
  };
  // Thin wrapper kept so the existing mouse-event call sites below don't
  // need to change -- getRelativePosAt is the one real implementation
  // both mouse and touch paths now share.
  const getRelativePos = (imgRef: React.RefObject<HTMLImageElement | null>, e: React.MouseEvent) =>
    getRelativePosAt(imgRef, e.clientX, e.clientY);
  const getImageOffset = (containerRef: React.RefObject<HTMLDivElement | null>, imgRef: React.RefObject<HTMLImageElement | null>) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    const img = imgRef.current;
    if (!containerRect || !img) return { x: 0, y: 0 };
    const imgRect = img.getBoundingClientRect();
    const content = getContainedImageRect(img);
    return { x: imgRect.left - containerRect.left + content.x, y: imgRect.top - containerRect.top + content.y };
  };

  const onCropMouseDown = (e: React.MouseEvent) => {
    const pos = getRelativePos(rawImgRef, e);
    setDragStart(pos);
    setSelection({ x: pos.x, y: pos.y, w: 0, h: 0 });
  };
  const onCropMouseMove = (e: React.MouseEvent) => {
    if (!dragStart) return;
    const pos = getRelativePos(rawImgRef, e);
    setSelection({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      w: Math.abs(pos.x - dragStart.x),
      h: Math.abs(pos.y - dragStart.y),
    });
  };
  const onCropMouseUp = () => setDragStart(null);

  // Real touch support -- the crop-selection drag here previously had
  // zero touch event handlers, the same confirmed bug found and fixed
  // in Resize & Crop: touch interactions on a phone or tablet don't
  // reliably fire mouse events for a drag/swipe gesture (only
  // touchstart/touchmove/touchend do), so selecting a region by
  // dragging simply never worked on a touch device -- exactly the
  // workflow (take a photo of a document, then crop it) this tool is
  // built around. e.preventDefault() on touchmove stops the page from
  // scrolling out from under the drag.
  const onCropTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 0) return;
    const t = e.touches[0];
    const pos = getRelativePosAt(rawImgRef, t.clientX, t.clientY);
    setDragStart(pos);
    setSelection({ x: pos.x, y: pos.y, w: 0, h: 0 });
  };
  const onCropTouchMove = (e: React.TouchEvent) => {
    if (!dragStart || e.touches.length === 0) return;
    e.preventDefault();
    const t = e.touches[0];
    const pos = getRelativePosAt(rawImgRef, t.clientX, t.clientY);
    setSelection({
      x: Math.min(dragStart.x, pos.x),
      y: Math.min(dragStart.y, pos.y),
      w: Math.abs(pos.x - dragStart.x),
      h: Math.abs(pos.y - dragStart.y),
    });
  };

  const applyCrop = () => {
    const img = rawImgRef.current;
    if (!img) return;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;

    if (selection && selection.w >= 4 && selection.h >= 4) {
      // Must use the same real, visible image content rect that
      // getRelativePosAt measured the selection against above -- the
      // raw element bounding box (which can be larger than the actual
      // visible image due to object-contain letterboxing) was the
      // exact bug that would otherwise produce a shifted/partial crop.
      const content = getContainedImageRect(img);
      const scaleX = img.naturalWidth / content.width;
      const scaleY = img.naturalHeight / content.height;
      canvas.width = Math.round(selection.w * scaleX);
      canvas.height = Math.round(selection.h * scaleY);
      ctx.drawImage(
        img,
        selection.x * scaleX, selection.y * scaleY, selection.w * scaleX, selection.h * scaleY,
        0, 0, canvas.width, canvas.height
      );
    } else {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
    }

    setCroppedUrl(canvas.toDataURL("image/png"));
    setStage("straighten");
  };

  // --- Straighten stage ---
  // Default the four corner handles to the image's own actual corners
  // -- if nothing is dragged, this is a safe no-op, so someone with an
  // already-straight photo can just skip through. Reset here (rather
  // than measuring immediately) because the <img> below hasn't
  // necessarily finished loading its real dimensions yet the moment
  // this stage first renders -- its own onLoad handler, not a
  // separate effect, is what triggers the actual corner measurement,
  // once the real DOM element (not a detached, never-mounted preload
  // image) genuinely has its final layout size.
  useEffect(() => {
    if (stage !== "straighten") return;
    setCorners(null);
  }, [stage, croppedUrl]);

  const handleStraightenImageLoad = () => {
    const img = straightenImgRef.current;
    if (!img) return;
    // Same letterbox-awareness fix as the crop stage -- corners need to
    // start at the actual visible image content's edges, not the full
    // (possibly letterboxed) element box, or the initial quad would be
    // wrong from the very first frame whenever this image's aspect
    // ratio doesn't exactly match its display box.
    const content = getContainedImageRect(img);
    if (content.width === 0 || content.height === 0) return;
    const full = {
      topLeft: { x: 0, y: 0 },
      topRight: { x: content.width, y: 0 },
      bottomRight: { x: content.width, y: content.height },
      bottomLeft: { x: 0, y: content.height },
    };
    // Try to find the page automatically; the person can still drag any corner.
    let found: QuadCorners | null = null;
    try {
      const c = document.createElement("canvas");
      const k = Math.min(1, 600 / Math.max(img.naturalWidth, img.naturalHeight));
      c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
      const cx = c.getContext("2d", { willReadFrequently: true })!;
      cx.drawImage(img, 0, 0, c.width, c.height);
      const q = detectDocumentQuad(cx.getImageData(0, 0, c.width, c.height));
      if (q) {
        const f = (p: { x: number; y: number }) => ({ x: (p.x / c.width) * content.width, y: (p.y / c.height) * content.height });
        found = { topLeft: f(q.topLeft), topRight: f(q.topRight), bottomRight: f(q.bottomRight), bottomLeft: f(q.bottomLeft) };
      }
    } catch (err) { console.warn("Page detection failed", err); }
    setAutoNote(found ? "Page found automatically. Check the corners and drag any that are off." : "Couldn't find the page edges automatically. Drag the four corners onto the page's corners. If you skip, the scan is a straight copy of this image, only cleaned up, not straightened.");
    setCorners(found ?? full);
  };

  // Manual 90 degree turn for photos that are still sideways; corners are re-detected on the turned image.
  const rotateCropped90 = () => {
    if (!croppedUrl) return;
    const im = new Image();
    im.onload = () => {
      const c = document.createElement("canvas");
      c.width = im.naturalHeight; c.height = im.naturalWidth;
      const cx = c.getContext("2d")!;
      cx.translate(c.width / 2, c.height / 2);
      cx.rotate(Math.PI / 2);
      cx.drawImage(im, -im.naturalWidth / 2, -im.naturalHeight / 2);
      setCroppedUrl(c.toDataURL("image/png"));
    };
    im.src = croppedUrl;
  };

  const onCornerPointerDown = (corner: keyof QuadCorners) => (e: React.PointerEvent) => {
    e.stopPropagation();
    setDraggingCorner(corner);
  };
  const onStraightenPointerMove = (e: React.PointerEvent) => {
    if (!draggingCorner) return;
    const pos = getRelativePos(straightenImgRef, e);
    setCorners(prev => prev ? { ...prev, [draggingCorner]: pos } : prev);
  };
  const onStraightenPointerUp = () => setDraggingCorner(null);

  const applyStraighten = () => {
    const img = straightenImgRef.current;
    if (!img || !corners) return;

    // Same consistency fix as handleStraightenImageLoad -- the scale
    // factor converting the on-screen corner positions back to real
    // image pixels must use the actual visible content area, not the
    // full (possibly letterboxed) element box.
    const content = getContainedImageRect(img);
    const scaleX = img.naturalWidth / content.width;
    const scaleY = img.naturalHeight / content.height;
    const scaled: QuadCorners = {
      topLeft: { x: corners.topLeft.x * scaleX, y: corners.topLeft.y * scaleY },
      topRight: { x: corners.topRight.x * scaleX, y: corners.topRight.y * scaleY },
      bottomRight: { x: corners.bottomRight.x * scaleX, y: corners.bottomRight.y * scaleY },
      bottomLeft: { x: corners.bottomLeft.x * scaleX, y: corners.bottomLeft.y * scaleY },
    };

    // Output size: the average of the top/bottom edge lengths and the
    // left/right edge lengths, in source pixels -- a reasonable size
    // estimate for the straightened result regardless of how skewed
    // the input quadrilateral was.
    const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(b.x - a.x, b.y - a.y);
    const outW = Math.round((dist(scaled.topLeft, scaled.topRight) + dist(scaled.bottomLeft, scaled.bottomRight)) / 2);
    const outH = Math.round((dist(scaled.topLeft, scaled.bottomLeft) + dist(scaled.topRight, scaled.bottomRight)) / 2);

    const straightened = perspectiveCorrect(img, scaled, Math.max(1, outW), Math.max(1, outH));
    const finalImg = new Image();
    finalImg.onload = () => {
      enhanceSourceImgRef.current = finalImg;
      setRotation(0);
      setStage("enhance");
    };
    finalImg.src = straightened.toDataURL("image/png");
  };

  const skipStraighten = () => {
    const img = straightenImgRef.current;
    if (!img) return;
    enhanceSourceImgRef.current = img;
    setRotation(0);
    setStage("enhance");
  };

  // --- Enhance stage (grayscale, contrast, rotate) ---
  const redrawEnhance = useCallback(() => {
    const sourceImg = enhanceSourceImgRef.current;
    const canvas = enhanceCanvasRef.current;
    if (!sourceImg || !canvas) return;

    let src: HTMLImageElement | HTMLCanvasElement = sourceImg;
    let flattened = true;
    if (removeShadows) {
      const flat = document.createElement("canvas");
      flat.width = sourceImg.naturalWidth; flat.height = sourceImg.naturalHeight;
      const fc = flat.getContext("2d", { willReadFrequently: true })!;
      fc.drawImage(sourceImg, 0, 0);
      const id = fc.getImageData(0, 0, flat.width, flat.height);
      flattened = flattenIllumination(id, Math.max(16, Math.round(Math.max(flat.width, flat.height) / 40)));
      fc.putImageData(id, 0, 0);
      src = flat;
    }
    const tone: Partial<ToneInfo> = {};
    let scanned = scanDocument(src, contrast[0], grayscale, tone);
    // Last line of defence (B-0253): whatever the filters did, never hand over a black page. Fall back to the original.
    let fellBack = false;
    try {
      const sc = scanned.getContext("2d", { willReadFrequently: true })!;
      if (isNearlyBlack(sc.getImageData(0, 0, scanned.width, scanned.height).data)) {
        const orig = document.createElement("canvas");
        orig.width = sourceImg.naturalWidth; orig.height = sourceImg.naturalHeight;
        orig.getContext("2d")!.drawImage(sourceImg, 0, 0);
        scanned = orig;
        fellBack = true;
      }
    } catch { /* if the pixels cannot be read, keep the scan as it is */ }
    setToneNote(
      fellBack
        ? "These settings made the page almost black, so the original photo is shown instead. Try turning off Grayscale or Remove shadows."
        : tone.lowContrast
        ? "This page has almost no contrast (a plain or flat coloured sheet), so shadow removal and contrast were skipped and the page was only brightened to stay readable."
        : tone.darkPage
          ? "This page is dark, so it was brightened instead of stretched towards black. Text that is lighter than the page may be faint."
          : !flattened ? "Shadow removal was skipped because the page has no uneven lighting to even out." : "",
    );
    const swap = rotation === 90 || rotation === 270;
    canvas.width = swap ? scanned.height : scanned.width;
    canvas.height = swap ? scanned.width : scanned.height;
    const ctx = canvas.getContext("2d")!;
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(scanned, -scanned.width / 2, -scanned.height / 2);
    ctx.restore();
  }, [contrast, rotation, grayscale, removeShadows]);

  useEffect(() => {
    if (stage === "enhance") redrawEnhance();
  }, [stage, redrawEnhance]);

  const rotate = () => setRotation(r => ((r + 90) % 360) as Rotation);


  const canvasToJpeg = (c: HTMLCanvasElement) => new Promise<Blob>((res, rej) => c.toBlob(b => (b ? res(b) : rej(new Error("encode failed"))), "image/jpeg", 0.92));

  const downloadPng = async () => {
    const canvas = enhanceCanvasRef.current;
    if (!canvas || !(await logUsageNow("download"))) return;
    canvas.toBlob(blob => { if (blob) downloadBlob(blob, `scanned_${fileName}.png`); }, "image/png");
  };

  const buildPdf = async (all: Blob[]) => {
    const pdf = await PDFDocument.create();
    for (const b of all) {
      const img = await pdf.embedJpg(new Uint8Array(await b.arrayBuffer()));
      const g = pdfPageFor(img.width, img.height, pageSize);
      const page = pdf.addPage([g.pageW, g.pageH]);
      page.drawImage(img, { x: (g.pageW - g.drawW) / 2, y: (g.pageH - g.drawH) / 2, width: g.drawW, height: g.drawH });
    }
    pdf.setProducer("FreeDailyPro");
    return new Blob([new Uint8Array(await pdf.save())], { type: "application/pdf" });
  };

  const downloadPdf = async () => {
    const canvas = enhanceCanvasRef.current;
    if (!canvas || !(await logUsageNow("download"))) return;
    try {
      downloadBlob(await buildPdf([...pages, await canvasToJpeg(canvas)]), `scanned_${fileName}${pages.length ? `_${pages.length + 1}pages` : ""}.pdf`);
    } catch (err) { console.error(err); toast.error("Couldn't build the PDF."); }
  };

  const addPage = async () => {
    const canvas = enhanceCanvasRef.current;
    if (!canvas) return;
    const jpg = await canvasToJpeg(canvas);
    setPages(p => [...p, jpg]);
    setStage("select"); setRawUrl(null); setCroppedUrl(null); setCorners(null); setSelection(null); setRotation(0);
    toast.success("Page added. Scan the next one.");
  };

  const reset = () => {
    setPages([]);
    setStage("select");
    setRawUrl(null);
    setCroppedUrl(null);
    setCorners(null);
    setSelection(null);
    setRotation(0);
  };

  return (
    <ToolContainer>
      {stage === "select" && (
        <div className="space-y-4">
          {pages.length > 0 && (
            <div className="space-y-2" data-testid="scan-pages">
              <p className="text-sm text-center">{pages.length} page{pages.length > 1 ? "s" : ""} added so far. Choose the next page's photo.</p>
              <ul className="max-w-sm mx-auto space-y-1">
                {pages.map((_, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="flex-1">Page {i + 1}</span>
                    <Button size="sm" variant="outline" disabled={i === 0} onClick={() => setPages(p => movePage(p, i, -1))} aria-label={`Move page ${i + 1} up`}>Up</Button>
                    <Button size="sm" variant="outline" disabled={i === pages.length - 1} onClick={() => setPages(p => movePage(p, i, 1))} aria-label={`Move page ${i + 1} down`}>Down</Button>
                    <Button size="sm" variant="ghost" onClick={() => setPages(p => p.filter((_, k) => k !== i))} aria-label={`Remove page ${i + 1}`}>Remove</Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <FileDropZone accept="image/*" onFiles={handleFiles} label="Drop a document photo to scan" description="Upload a photo of a document to enhance" deferUsageLog />
        </div>
      )}

      {stage === "crop" && rawUrl && (
        <div className="space-y-4">
          <div
            ref={rawContainerRef}
            className="relative w-full rounded-lg border max-h-[500px] overflow-hidden bg-muted/30 cursor-crosshair select-none"
            onMouseDown={onCropMouseDown}
            onMouseMove={onCropMouseMove}
            onMouseUp={onCropMouseUp}
            onMouseLeave={onCropMouseUp}
            onTouchStart={onCropTouchStart}
            onTouchMove={onCropTouchMove}
            onTouchEnd={onCropMouseUp}
            onTouchCancel={onCropMouseUp}
            style={{ touchAction: "none" }}
          >
            <img ref={rawImgRef} src={rawUrl} alt="Document photo" className="w-full max-h-[500px] object-contain pointer-events-none" draggable={false} />
            {selection && selection.w >= 4 && selection.h >= 4 && (() => {
              const offset = getImageOffset(rawContainerRef, rawImgRef);
              return (
                <div
                  className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
                  style={{ left: offset.x + selection.x, top: offset.y + selection.y, width: selection.w, height: selection.h }}
                />
              );
            })()}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Drag to crop out anything that isn't the document, or continue to use the whole photo.
          </p>
          {lowResNote && <NoticeBox className="text-xs text-center" role="status">{lowResNote}</NoticeBox>}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button onClick={applyCrop} >Continue</Button>
            <Button variant="outline" onClick={reset}>Take Another</Button>
          </div>
        </div>
      )}

      {stage === "straighten" && croppedUrl && (
        <div className="space-y-4">
          <div
            ref={straightenContainerRef}
            className="relative w-full rounded-lg border max-h-[500px] overflow-hidden bg-muted/30 select-none"
            onPointerMove={onStraightenPointerMove}
            onPointerUp={onStraightenPointerUp}
            onPointerLeave={onStraightenPointerUp}
          >
            <img ref={straightenImgRef} src={croppedUrl} alt="Cropped document" onLoad={handleStraightenImageLoad} className="w-full max-h-[500px] object-contain pointer-events-none" draggable={false} />
            {corners && (() => {
              const offset = getImageOffset(straightenContainerRef, straightenImgRef);
              const cornerList: (keyof QuadCorners)[] = ["topLeft", "topRight", "bottomRight", "bottomLeft"];
              return (
                <>
                  <svg className="absolute inset-0 pointer-events-none" style={{ left: offset.x, top: offset.y }}>
                    <polygon
                      points={cornerList.map(c => `${corners[c].x},${corners[c].y}`).join(" ")}
                      fill="rgba(59,130,246,0.15)" stroke="rgb(59,130,246)" strokeWidth={2}
                    />
                  </svg>
                  {cornerList.map(c => (
                    <div
                      key={c}
                      onPointerDown={onCornerPointerDown(c)}
                      className="absolute h-5 w-5 rounded-full bg-primary border-2 border-white shadow cursor-grab active:cursor-grabbing touch-none"
                      style={{ left: offset.x + corners[c].x - 10, top: offset.y + corners[c].y - 10 }}
                    />
                  ))}
                </>
              );
            })()}
          </div>
          <p className="text-xs text-muted-foreground text-center">
            {corners
              ? (autoNote || "Drag the four corner handles onto the document's corners to straighten it. Skip if it's already straight-on.")
              : "Loading..."}
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button onClick={applyStraighten} disabled={!corners} className="gap-2">Straighten</Button>
            <Button variant="outline" onClick={skipStraighten}>Skip — Already Straight</Button>
            <Button variant="outline" onClick={rotateCropped90} className="gap-2" aria-label="Rotate the photo 90 degrees clockwise"><RotateCw className="h-4 w-4" /> Rotate 90</Button>
          </div>
        </div>
      )}

      {stage === "enhance" && (
        <div className="space-y-4">
          <canvas ref={enhanceCanvasRef} className="w-full max-h-96 object-contain rounded-lg border mx-auto block" />
          {toneNote && <NoticeBox className="text-xs text-center" role="status">{toneNote}</NoticeBox>}

          <div className="max-w-sm mx-auto space-y-3">
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant={grayscale ? "default" : "outline"} onClick={() => setGrayscale(true)}>Grayscale</Button>
              <Button size="sm" variant={!grayscale ? "default" : "outline"} onClick={() => setGrayscale(false)}>Keep Color</Button>
            </div>
            <label className="flex items-center gap-2 text-sm justify-center"><input type="checkbox" checked={removeShadows} onChange={e => setRemoveShadows(e.target.checked)} /> Remove shadows / even out lighting</label>
            <label className="flex items-center gap-2 text-sm justify-center">PDF page
              <select className="border rounded px-1 bg-background" value={pageSize} onChange={e => setPageSize(e.target.value as ScanPageSize)}>
                <option value="a4">A4</option><option value="letter">US Letter</option><option value="fit">Fit the scan (150 dpi)</option>
              </select>
            </label>
            <div>
              <Label className="text-xs">Contrast: {contrast[0]}</Label>
              <Slider value={contrast} onValueChange={setContrast} min={0} max={120} step={5} />
            </div>
          </div>

          <div className="flex gap-2 justify-center">
            <Button size="sm" variant="outline" onClick={rotate} className="gap-2"><RotateCw className="h-4 w-4" /> Rotate</Button>
          </div>

          <div className="flex gap-3 justify-center flex-wrap">
            <Button onClick={() => void downloadPdf()} className="gap-2">Download PDF{pages.length ? ` (${pages.length + 1} pages)` : ""}</Button>
            <Button variant="outline" onClick={() => void downloadPng()}>Download PNG</Button>
            <Button variant="outline" onClick={() => void addPage()}>Add page &amp; scan next</Button>
            <Button variant="outline" onClick={reset}>New</Button>
          </div>
        </div>
      )}

    </ToolContainer>
  );
}
